/* import __COLOCATED_TEMPLATE__ from './unassign-unsnoozed-setting.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';

type WhenAwayMode = 'when_away_only' | 'when_away_and_reassign';

interface DropdownItem {
  text: string;
  value: string;
}

interface Args {
  unassignUnsnoozedAtCapacityEnabled: boolean;
  unassignUnsnoozedAtCapacityUpdate(value: boolean): void;
  unassignUnsnoozedWhenAwayEnabled: boolean;
  unassignUnsnoozedWhenAwayAndReassignEnabled: boolean;
  unassignUnsnoozedWhenAwayUpdate(value: boolean, mode: WhenAwayMode): void;
}

interface Signature {
  Args: Args;
  Element: never;
}

export default class UnassignUnsnoozedSetting extends Component<Signature> {
  @service declare intl: IntlService;

  @tracked _lastSelectedMode: WhenAwayMode | null = null;

  get lastSelectedMode(): WhenAwayMode {
    return this._lastSelectedMode ?? this.selectedWhenAwayMode;
  }

  set lastSelectedMode(mode: WhenAwayMode) {
    this._lastSelectedMode = mode;
  }

  @action
  toggleUnassignUnsnoozedAtCapacity() {
    let newValue = !this.args.unassignUnsnoozedAtCapacityEnabled;
    this.args.unassignUnsnoozedAtCapacityUpdate(newValue);
  }

  get whenAwayMasterEnabled(): boolean {
    return (
      this.args.unassignUnsnoozedWhenAwayEnabled ||
      this.args.unassignUnsnoozedWhenAwayAndReassignEnabled
    );
  }

  get selectedWhenAwayMode(): WhenAwayMode {
    if (this.args.unassignUnsnoozedWhenAwayAndReassignEnabled) {
      return 'when_away_and_reassign';
    }
    return 'when_away_only';
  }

  get whenAwayDropdownItems(): DropdownItem[] {
    return [
      {
        text: this.intl.t(
          'settings.workload-management.unassign-unsnoozed.when-away-dropdown-away',
        ),
        value: 'when_away_only',
      },
      {
        text: this.intl.t(
          'settings.workload-management.unassign-unsnoozed.when-away-dropdown-away-and-reassign',
        ),
        value: 'when_away_and_reassign',
      },
    ];
  }

  @action
  toggleWhenAway() {
    if (this.whenAwayMasterEnabled) {
      this.lastSelectedMode = this.selectedWhenAwayMode;
      this.args.unassignUnsnoozedWhenAwayUpdate(false, this.selectedWhenAwayMode);
    } else {
      this.args.unassignUnsnoozedWhenAwayUpdate(true, this.lastSelectedMode);
    }
  }

  @action
  selectWhenAwayMode(mode: WhenAwayMode) {
    if (mode === this.lastSelectedMode) {
      return;
    }
    this.lastSelectedMode = mode;
    if (this.whenAwayMasterEnabled) {
      this.args.unassignUnsnoozedWhenAwayUpdate(true, mode);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox::WorkloadManagement::UnassignUnsnoozedSetting': typeof UnassignUnsnoozedSetting;
    'inbox/workload-management/unassign-unsnoozed-setting': typeof UnassignUnsnoozedSetting;
  }
}
